import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import KeyHolders from './KeyHolders'
import KeyShares from './KeyShares'

import {REQUIRED_CONTACTS} from '../../../lib/recovery'

/**
 * The ask, and the one screen in a recovery where the slots graphic tells the truth — every contact
 * shown is holding a piece right now.
 *
 * Contacts who never accepted, or whose handover never finished, are left off entirely. The API will
 * not write to them, and listing them would raise a question this screen cannot answer.
 */
const AskContacts = ({error = '', ...props}) => {
  const {holders} = props
  const enough = holders.length >= REQUIRED_CONTACTS
  const shortfall = {
    0: 'None of your contacts is holding a piece of your key',
    1: 'Only one of your contacts is holding a piece of your key'
  }[holders.length] || `Only ${holders.length} of your contacts are holding a piece of your key`

  return (
    <Paper elevation={3} component='div' className='recovery-step'>
      <Typography variant='h4' gutterBottom>Ask your contacts for their pieces</Typography>
      <Typography variant='body1' gutterBottom>
        These people each hold a piece of your key. We'll email them a link, and they'll be asked for
        the password they chose when they agreed to help.
      </Typography>
      <Stack
        className='strength'
        component='div'
        sx={{alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between'}}>
        <Stack className='reading' component='div'>
          <Typography className='headline' variant='body1'>Any three is enough</Typography>
          <Typography variant='body2'>You don't need to wait for everyone.</Typography>
        </Stack>
        <KeyShares ready={holders.length} />
      </Stack>
      <KeyHolders holders={holders} chip={{label: 'Holds a piece', color: 'success'}} />
      {
        enough ? (
          <Button
            className='action'
            disabled={props.loading}
            onClick={props.onAsk}
            variant='contained'
            sx={{color: '#F7F7F7'}}>
            Ask them now
          </Button>
        ) : (
          <Alert className='notice' severity='error'>
            <AlertTitle>There aren't enough pieces to recover with</AlertTitle>
            {shortfall}, and it takes three. New pieces can only be handed out from an account that
            isn't locked, so this can't be put right from here.
          </Alert>
        )
      }
      {error.length > 0 && <Typography variant='body1' color='error'>{error}</Typography>}
    </Paper>
  )
}

AskContacts.propTypes = {
  holders: PropTypes.array.isRequired,
  onAsk: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool
}

export default AskContacts
